<template>
  <div class="flex flex-col gap-[16px]">
    <!-- One-time (C1 copy 2, D4, D5, V). The frames draw the amount as the form's
         input; it is read-only here, as every other summary card is. -->
    <DsCard
      v-if="summary.oneTime > 0"
      class="flex flex-col gap-[10px]"
      data-test="bf-summary-one-time"
    >
      <PledgeCardTitle tone="oneTime">I am ready to give a one-time amount of:</PledgeCardTitle>
      <div :class="totalRowClass">
        <span>Total</span>
        <span class="text-[20px]" data-test="bf-summary-one-time-total">{{
          formatAmount(summary.oneTime)
        }}</span>
      </div>
      <!-- D4b: a first-timer's tick closes the card. -->
      <div
        v-if="firstTimeIn === 'oneTime'"
        :class="['pt-[16px]', tickRowClass]"
        data-test="bf-summary-first-time"
      >
        <span :class="tickCircleClass">
          <DsIcon name="check" size="md" />
        </span>
        <span>{{ firstTimeLabel }}</span>
      </div>
    </DsCard>

    <!-- Months: only when something was pledged over the five months (D1, D3). -->
    <DsCard v-if="summary.total > 0" class="flex flex-col gap-[10px]" data-test="bf-summary-months">
      <PledgeCardTitle>Over the next 5 months,<br />I commit to giving</PledgeCardTitle>
      <!-- Total first, then the months, as the D frames have it. -->
      <div :class="totalRowClass">
        <span>Total</span>
        <span class="text-[20px]" data-test="bf-summary-total">{{
          formatAmount(summary.total)
        }}</span>
      </div>
      <div class="flex flex-wrap gap-[10px] mt-[4px] mb-[2px]">
        <div
          v-for="month in summary.months"
          :key="month.key"
          class="flex flex-col gap-[2px] w-[calc(50%-5px)] px-[14px] py-[10px] rounded-[10px] bg-white border border-solid border-campaign-line-soft"
          :data-test="`bf-summary-${month.key}`"
        >
          <DsCaption
            class="text-[12px] leading-[16px] font-bold tracking-[0.04em] text-campaign-subtle"
          >
            {{ month.label }}
          </DsCaption>
          <!-- A month left at 0 is shown as a dash so the grid keeps its five tiles. -->
          <DsBody class="text-[18px] leading-[24px] font-bold text-campaign-ink">
            {{ month.amount ? formatAmount(month.amount) : '—' }}
          </DsBody>
        </div>
      </div>
      <!-- C1, D1b, D3b: a first-timer's tick closes the card. -->
      <div
        v-if="firstTimeIn === 'months'"
        :class="['pt-[16px]', tickRowClass]"
        data-test="bf-summary-first-time"
      >
        <span :class="tickCircleClass">
          <DsIcon name="check" size="md" />
        </span>
        <span>{{ firstTimeLabel }}</span>
      </div>
    </DsCard>

    <!-- Sow (D2, D3). -->
    <DsCard v-if="summary.sow" class="flex flex-col gap-[10px]" data-test="bf-summary-sow">
      <PledgeCardTitle tone="sow">I pledge to sow...</PledgeCardTitle>
      <DsBody class="text-text-body-md text-campaign-ink whitespace-pre-line break-words">
        {{ summary.sow }}
      </DsBody>
    </DsCard>

    <!-- Couple (D3, V): the youth card's tick row, the partner named inline. -->
    <DsCard v-if="summary.couple" :class="tickCardClass" data-test="bf-summary-couple">
      <div :class="tickRowClass">
        <span :class="tickCircleClass">
          <DsIcon name="check" size="md" />
        </span>
        <span class="min-w-0 break-words">
          Pledging as a couple<template v-if="summary.couple.partnerName">
            with
            <span data-test="bf-summary-partner-name">{{ summary.couple.partnerName }}</span>
          </template>
          👩🏻‍❤️‍👨🏻
        </span>
      </div>
    </DsCard>

    <!-- Youth. -->
    <DsCard v-if="summary.youth" :class="tickCardClass" data-test="bf-summary-youth">
      <div :class="tickRowClass">
        <span :class="tickCircleClass">
          <DsIcon name="check" size="md" />
        </span>
        <span>Currently in Secondary School OR aged 16 and below</span>
      </div>
      <!-- As on the form before submit. V hides these cards from youths, so in
           practice it is D's. -->
      <DsAlert
        v-if="summary.youthExceeded"
        variant="warning"
        class="mt-[12px]"
        data-test="bf-summary-youth-notice"
      >
        {{ youthNotice }}
      </DsAlert>
    </DsCard>
  </div>
</template>

<script>
import {
  Alert as DsAlert,
  Body as DsBody,
  Caption as DsCaption,
  Card as DsCard,
  Icon as DsIcon,
} from '@team-hogc/hogc-one-design-system';
import { BF_FIRST_TIME_LABEL, BF_YOUTH_NOTICE } from '@/constants/MyGiving/building-fund-pledge';
import { formatPledgeAmount } from '@/utils/building-fund-pledge';
import PledgeCardTitle from './PledgeCardTitle.vue';

// The summary cards shared by the BF2026 confirm page (C), done page (D1–D5) and
// view page (V): only the cards the pledge has, in the frames' order. Takes
// `pledgeSummary()`'s shape, so it renders the submitted body and giving-api's
// pledge alike.
export default {
  name: 'PledgeSummary',

  components: { DsAlert, DsBody, DsCaption, DsCard, DsIcon, PledgeCardTitle },

  props: {
    summary: {
      type: Object,
      required: true,
    },
    // Whether to show a first-timer's tick: only where a frame draws it, which the
    // page decides (C1, D1b, D3b, D4b; never V).
    showFirstTime: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      youthNotice: BF_YOUTH_NOTICE,
      firstTimeLabel: BF_FIRST_TIME_LABEL,
      totalRowClass:
        'flex justify-between items-center px-[16px] py-[12px] rounded-[12px] bg-campaign-tint text-[16px] font-extrabold text-campaign-primary',
      tickCardClass: '[--hogc-card-padding:16px_20px]',
      tickCircleClass:
        'size-[32px] shrink-0 rounded-full bg-campaign-highlight text-campaign-ink flex items-center justify-center',
      tickRowClass:
        'flex items-center gap-[10px] text-[15px] leading-[22px] font-semibold text-campaign-ink',
    };
  },

  computed: {
    // The card the tick closes: the months, else the one-time amount.
    firstTimeIn() {
      if (!this.showFirstTime || !this.summary.firstTime) return null;
      if (this.summary.total > 0) return 'months';
      return this.summary.oneTime > 0 ? 'oneTime' : null;
    },
  },

  methods: {
    formatAmount: formatPledgeAmount,
  },
};
</script>
